import { Card, Badge, Alert, Modal } from "flowbite-react";
import React, { useMemo } from "react";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import Protestos from "../../common/query/Protestos";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import AvisoCard from "../../common/query/AvisoCard";
import { EvaluationScore } from "../../common/query/EvaluationScore";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import ChequesSustados from "../../common/query/ChequesSustados";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import { formatDocument } from "../../../utils/utils";
// import ReactApexChart from "react-apexcharts";
import CreditQuadrants from "./CreditQuadrants";
import ScoreRating from "./ScoreRating";
import { HiOutlineEye } from "react-icons/hi";
import StreetView from "../../common/query/StreetView";
import DecisionBannerPF from "../DecisionBannerPF";
import { FaRegCalendarAlt, FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
} from "react-icons/gr";
import { CiCircleCheck, CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import { IoMdNotificationsOutline } from "react-icons/io";
import { BsBank } from "react-icons/bs";
import moment from "moment";

/** Helpers locais */
const formatCurrency = (val) => {
  if (val === null || val === undefined) return "R$ 0,00";
  try {
    return Number(val).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  } catch {
    const num = Number(val) || 0;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
};

const safeJoin = (arr) =>
  Array.isArray(arr) ? arr.filter(Boolean).join(", ") : arr || "Indisponível";

const fmt = (v, dash = "0") =>
  v === null || v === undefined || v === "-" ? dash : v;

const Bar = ({ value = 0, max = 1000, title }) => {
  // normaliza para 0..100 (evita overflow se valor > max)
  const pct = Math.max(
    0,
    Math.min(100, Math.round((Number(value) / Number(max || 1)) * 100))
  );
  return (
    <div className="w-full">
      {title && (
        <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
          <span>{title}</span>
          <span className="font-medium text-gray-700">{fmt(value)}</span>
        </div>
      )}
      <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-2.5 bg-blue-600 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const Pill = ({ label, value, color = "info" }) => (
  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 gap-2">
    <span className="text-xs text-gray-500">{label}</span>
    <Badge color={color}>{fmt(value, "-")}</Badge>
  </div>
);

const StatItem = ({ label, value }) => (
  <div className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-200">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="font-semibold text-gray-800">{fmt(value)}</span>
  </div>
);

const TabelaResumoMeses = ({ resumo }) => {
  if (!resumo) return null;
  const linhas = [
    {
      rotulo: "Último mês",
      periodo: resumo?.ultimoMes?.periodo,
      total: resumo?.ultimoMes?.total,
    },
    {
      rotulo: "Penúltimo mês",
      periodo: resumo?.penultimoMes?.periodo,
      total: resumo?.penultimoMes?.total,
    },
    {
      rotulo: "Antepenúltimo mês",
      periodo: resumo?.antepenultimoMes?.periodo,
      total: resumo?.antepenultimoMes?.total,
    },
  ];
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-3 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
        <div>Período</div>
        <div className="text-center">Mês/Ano</div>
        <div className="text-right">Consultas</div>
      </div>
      {linhas.map((l, i) => (
        <div
          key={i}
          className="grid grid-cols-3 px-3 py-2 text-sm border-t border-gray-100"
        >
          <div>{l.rotulo}</div>
          <div className="text-center">{l.periodo || "-"}</div>
          <div className="text-right">{l.total ?? 0}</div>
        </div>
      ))}
      <div className="grid grid-cols-3 px-3 py-2 text-sm border-t border-gray-200 bg-gray-50">
        <div className="font-semibold">Total no período</div>
        <div className="text-center">
          {resumo?.periodoInicial || "-"} → {resumo?.periodoFinal || "-"}
        </div>
        <div className="text-right font-semibold">{resumo?.total ?? 0}</div>
      </div>
    </div>
  );
};

// mapeia a nota A..E para índice, cor e % no trilho
const notaInfo = (notaRaw) => {
  const n = String(notaRaw || "")
    .toUpperCase()
    .trim();
  const mapa = { A: 0, B: 1, C: 2, D: 3, E: 4 };
  const idx = n in mapa ? mapa[n] : null;
  const pct = idx === null ? 0 : (idx / 4) * 100; // 0% A ... 100% E
  const tailwind = ["#16a34a", "#65a30d", "#eab308", "#f59e0b", "#ef4444"]; // fallback

  // cor de badge Flowbite
  const badge =
    idx === 0
      ? "success"
      : idx === 1
      ? "green"
      : idx === 2
      ? "warning"
      : idx === 3
      ? "orange"
      : "failure"; // E

  return {
    idx,
    pct,
    cor: idx === null ? "#6b7280" : tailwind[idx],
    badge,
    nota: n || "-",
  };
};

const TrilhoNota = ({ nota }) => {
  const { pct } = notaInfo(nota);
  return (
    <div className="mt-3 px-2">
      {/* gradiente A→E: verde→vermelho */}
      <div className="h-2.5 w-full rounded-full overflow-hidden bg-gradient-to-r from-green-600 via-yellow-400 to-red-500" />
      {/* marcador */}
      <div className="relative h-0">
        <div
          className="absolute -top-4"
          style={{ left: `calc(${pct}% - 10px)` }}
        >
          <div className="w-5 h-5 rounded-full border-2 border-white shadow ring-2 ring-black/10 bg-white flex items-center justify-center text-[10px] font-bold">
            {String(nota || "-").toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiNota = ({ titulo, nota, descricao }) => {
  const { nota: n, badge } = notaInfo(nota);
  return (
    <div className="border border-gray-200 rounded-md shadow-md p-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 mb-1">{titulo}</div>
          <div className="text-3xl font-extrabold leading-tight">{n}</div>
        </div>
        <Badge color={badge} className="text-xs">
          {n === "-" ? "N/D" : `Nota ${n}`}
        </Badge>
      </div>
      <p className="text-xs text-gray-500 mt-1">{descricao}</p>
      <TrilhoNota nota={n} />
    </div>
  );
};

const AcertaCompletoPositivoQuery = ({ data, dataFull }) => {
  const dados = data?.dadosCadastrais || {};
  const [openMap, setOpenMap] = React.useState(false);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.numero,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade,
    data?.dadosCadastrais?.cep,
  ]
    .filter(Boolean)
    .join(", ");

  const cpfFormatted = formatDocument(dados?.cpf);
  const birthMoment = dados?.dataNascimento
    ? moment(dados?.dataNascimento, ["DD/MM/YYYY", "YYYY-MM-DD"])
    : null;
  const birthLabel = birthMoment?.isValid()
    ? birthMoment.format("DD/MM/YYYY")
    : null;
  const ageYears =
    birthMoment?.isValid() && birthMoment.isBefore(moment())
      ? moment().diff(birthMoment, "years")
      : null;
  const ageLabel =
    ageYears != null ? `${ageYears} ano${ageYears === 1 ? "" : "s"}` : null;
  const emailValue = safeJoin(dados?.emails || dados?.email);
  const telefoneFixo = safeJoin(dados?.telefoneFixo);
  const celular = safeJoin(dados?.celular || dados?.telefone);

  // Resumo padronizado (padrão acordado)
  const resumo = useMemo(() => {
    const r = data?.resumoConsulta || {};
    return {
      protestos: {
        quantidadeTotal: r?.protestos?.quantidadeTotal ?? 0,
        valorTotal: r?.protestos?.valorTotal ?? 0,
      },
      pendenciasFinanceiras: {
        quantidadeTotal: r?.pendenciasFinanceiras?.quantidadeTotal ?? 0,
        valorTotal: r?.pendenciasFinanceiras?.valorTotal ?? 0,
      },
      acoesCiveis: {
        quantidadeTotal: r?.acoesCiveis?.quantidadeTotal ?? 0,
        valorTotal: r?.acoesCiveis?.valorTotal ?? 0,
      },
      falenciasRecJudiciais: {
        quantidadeTotal: r?.falenciasRecJudiciais?.quantidadeTotal ?? 0,
        ultimaOcorrencia: r?.falenciasRecJudiciais?.ultimaOcorrencia || null,
      },
      chequesSemFundo: {
        quantidadeTotal: r?.chequesSemFundo?.quantidadeTotal ?? 0,
      },
      chequesSustados: {
        quantidadeTotal: r?.chequesSustados?.quantidadeTotal ?? 0,
      },
    };
  }, [data]);

  // Avisos opcionais
  const avisos = Array.isArray(data?.avisos) ? data?.avisos : [];

  return (
    <div>
      {/* Opcional:
      <CreditQuadrants
        suggestion="recomendado"
        score={data?.score?.score}
        scoreMax={1000}
        pd={data?.score?.probabilidade}
      />
      */}
      {data?.decisaoExtra?.scoreCodigo ? (
        <DecisionBannerPF
          compact={true}
          flag={data?.decisaoExtra?.scoreCodigo}
        />
      ) : null}

      {/* ===================== DADOS CADASTRAIS ===================== */}
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FiUserCheck className="text-blue-700" />{" "}
          <span>Dados Cadastrais</span>
        </div>
      </TitleStripe>

      <div className="mt-4 text-xs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Nome completo
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {dados?.nome || "Indisponível"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Situação do CPF
            </p>
            <span
              className={`inline-flex items-center rounded-full text-[11px] px-3 py-1 font-semibold ${
                (dados?.situacao || "").toUpperCase() === "REGULAR"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {dados?.situacao || "Indisponível"}
            </span>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
          {cpfFormatted && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              CPF: {cpfFormatted}
            </span>
          )}
          {birthLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              Data Nasc: {birthLabel}
            </span>
          )}
          {ageLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              {ageLabel}
            </span>
          )}
        </div>

        <div className="mt-3 grid gap-2 grid-cols-2">
          {[
            {
              label: "Nome da mãe",
              value: dados?.nomeMae || "Não informado",
              hint: "Parente",
            },
            {
              label: "Origem CPF",
              value: dados?.regiaoCPF || "Não informado",
              hint: "Origem",
            },
            {
              label: "Sexo",
              value: dados?.sexo || "Não informado",
              hint: "Gênero",
            },
            {
              label: "Estado civil",
              value: dados?.estadoCivil || "Não informado",
              hint: "Situação pessoal",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>

        <div className="mt-2 grid gap-2 grid-cols-2">
          {[
            {
              label: "Grau de instrução",
              value: dados?.grauInstrucao || "Não informado",
              hint: "Formação",
            },
            {
              label: "Título de eleitor",
              value: dados?.tituloEleitor || "Não informado",
              hint: "Documento",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>

        <div className="mt-2 grid gap-2 grid-cols-2">
          {[
            {
              label: "Celular",
              value: celular || "Não informado",
            },
            {
              label: "E-mail",
              value: dados?.email || "Não informado",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <LuMapPin className="text-blue-700" /> <span>Localização</span>
          </div>
        </TitleStripe>

        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Logradouro
            </p>
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.endereco || "—"}
            </p>
            {fullAddress && (
              <div
                className="flex items-center gap-1 text-[11px] text-blue-600 cursor-pointer mt-1"
                onClick={() => setOpenMap(true)}
              >
                <span>Ver fotos do local</span>
                <HiOutlineEye className="text-base" />
              </div>
            )}
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Bairro
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.bairro || "--"}</span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Cidade
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.cidade || "--"}</span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              CEP
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.cep || "--"}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ===================== AVALIAÇÃO / SCORE ===================== */}
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FaRegChartBar className="text-blue-700" />{" "}
          <span>Avaliação preliminar de crédito</span>
        </div>
      </TitleStripe>
      <EvaluationScore
        data={data}
        score={data?.score?.score}
        probability={data?.score?.probabilidade}
        mensagem={data?.score?.mensagem}
      />

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      {/* ===================== DECISÃO & LIMITE DE PARCELA ===================== */}
      {data?.limiteParcela && (
        <div>
          <div className="gap-4 border border-gray-100 rounded-md p-3 bg-gray-50 mt-4">
            <h3 className="font-semibold flex items-center gap-2">
              <BsBank className="text-blue-700" />{" "}
              <span>Limite de parcela</span>
            </h3>
            <div className="text-sm mt-2">
              {data?.limiteParcela?.parcelaMensalSugerida ? (
                <>
                  <div className="text-gray-500 text-xs">
                    Parcela mensal sugerida
                  </div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(data?.limiteParcela?.parcelaMensalSugerida)}
                  </div>
                </>
              ) : null}

              <div className="mt-2 text-gray-700">
                <div className="text-xs text-gray-500">Descrição</div>
                <div>{data?.limiteParcela?.descricao || "-"}</div>
              </div>
            </div>
          </div>
          <div className="mt-1 text-gray-500 italic text-xs">
            {data?.limiteParcela?.detalhes || "-"}
          </div>
        </div>
      )}

      <div>
        <TitleStripe className="mb-1">
          <CiCircleCheck className="inline-flex text-blue-700" /> Painel
          positivo
        </TitleStripe>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {["A", "B", "C", "D", "E"].includes(
            String(
              data?.painelPositivo?.pontualidadePagamento?.notaFaturaEmAtraso ??
                ""
            ).toUpperCase()
          ) && (
            <KpiNota
              titulo="Fatura em atraso (nota)"
              nota={String(
                data?.painelPositivo?.pontualidadePagamento?.notaFaturaEmAtraso
              ).toUpperCase()}
              descricao="Quanto mais perto de E, pior (mais dias em atraso)."
            />
          )}

          {["A", "B", "C", "D", "E"].includes(
            String(
              data?.painelPositivo?.pontualidadePagamento?.notaAdiantamento ??
                ""
            ).toUpperCase()
          ) && (
            <KpiNota
              titulo="Pagamento adiantado (nota)"
              nota={String(
                data?.painelPositivo?.pontualidadePagamento?.notaAdiantamento
              ).toUpperCase()}
              descricao="Quanto mais perto de E, pior."
            />
          )}

          {["A", "B", "C", "D", "E"].includes(
            String(
              data?.painelPositivo?.consumo?.notaContratosRecentes ?? ""
            ).toUpperCase()
          ) && (
            <KpiNota
              titulo="Contratos recentes (nota)"
              nota={String(
                data?.painelPositivo?.consumo?.notaContratosRecentes
              ).toUpperCase()}
              descricao="Quanto mais perto de E, pior."
            />
          )}
        </div>

        {data?.painelPositivo?.maturidadeCredito?.mesesContratoMaisAntigo && (
          <div className="grid grid-cols-1 xl:grid-cols-1 gap-4 mb-4">
            <div className="border border-gray-200 rounded-md p-3">
              <h4 className="font-semibold mb-1">Maturidade de crédito</h4>

              <div className="font-semibold text-lg">
                {
                  data?.painelPositivo?.maturidadeCredito
                    ?.mesesContratoMaisAntigo
                }
                m
              </div>

              <div className="mt-2 text-gray-600">
                {data?.painelPositivo?.maturidadeCredito?.textoExperiencia}
              </div>
            </div>
          </div>
        )}

        {/* Cards principais */}
        <div className="grid grid-cols-1 xl:grid-cols-1 gap-4">
          {/* 1) Pontualidade */}
          <div className="border border-gray-200 rounded-md p-3">
            <h4 className="font-semibold mb-1">Pontualidade de pagamento</h4>
            <p className="text-xs text-gray-500 mb-3">
              Distribuição de pagamentos por categoria.
            </p>

            {/* Operações Parceladas */}
            <div className="mb-3">
              <div className="text-xs font-semibold text-gray-600 mb-2">
                Operações parceladas
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.pagosEmDia ? (
                  <StatItem
                    label="Pagos em dia"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagosEmDia
                    }
                  />
                ) : null}
                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.pagosAte5Dias ? (
                  <StatItem
                    label="Até 5 dias"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagosAte5Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.pagos6a30Dias ? (
                  <StatItem
                    label="6 a 30 dias"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagos6a30Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.pagosApos31Dias ? (
                  <StatItem
                    label="Após 31 dias"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagosApos31Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.semInfo5a30Dias ? (
                  <StatItem
                    label="Sem info (5–30)"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.semInfo5a30Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.semInfoApos31Dias ? (
                  <StatItem
                    label="Sem info (+31)"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.semInfoApos31Dias
                    }
                  />
                ) : null}
              </div>

              {/* barras comparativas (foco no “em dia” vs “atrasos”) */}
              <div className="space-y-2">
                {data?.painelPositivo?.pontualidadePagamento
                  ?.operacoesParceladas?.pagosEmDia ? (
                  <Bar
                    title="Pagos em dia (força relativa)"
                    value={
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagosEmDia
                    }
                    max={[
                      "pagosEmDia",
                      "pagosAte5Dias",
                      "pagos6a30Dias",
                      "pagosApos31Dias",
                    ]
                      .map((k) =>
                        Number(
                          data?.painelPositivo?.pontualidadePagamento
                            ?.operacoesParceladas?.[k] || 0
                        )
                      )
                      .reduce((a, b) => Math.max(a, b), 1)}
                  />
                ) : null}

                <Bar
                  title="Atrasos (até 30 dias)"
                  value={
                    Number(
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagosAte5Dias || 0
                    ) +
                    Number(
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagos6a30Dias || 0
                    )
                  }
                  max={
                    Number(
                      data?.painelPositivo?.pontualidadePagamento
                        ?.operacoesParceladas?.pagosEmDia || 0
                    ) || 1
                  }
                />
              </div>
            </div>

            <div className="mb-3 mt-3">
              <div className="text-xs font-semibold text-gray-600 mb-2">
                Cartão de crédito
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.pagosEmDia ? (
                  <StatItem
                    label="Pagos em dia"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagosEmDia
                    }
                  />
                ) : null}
                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.pagosAte5Dias ? (
                  <StatItem
                    label="Até 5 dias"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagosAte5Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.pagos6a30Dias ? (
                  <StatItem
                    label="6 a 30 dias"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagos6a30Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.pagosApos31Dias ? (
                  <StatItem
                    label="Após 31 dias"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagosApos31Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.semInfo5a30Dias ? (
                  <StatItem
                    label="Sem info (5–30)"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.semInfo5a30Dias
                    }
                  />
                ) : null}

                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.semInfoApos31Dias ? (
                  <StatItem
                    label="Sem info (+31)"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.semInfoApos31Dias
                    }
                  />
                ) : null}
              </div>

              {/* barras comparativas (foco no “em dia” vs “atrasos”) */}
              <div className="space-y-2">
                {data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                  ?.pagosEmDia ? (
                  <Bar
                    title="Pagos em dia (força relativa)"
                    value={
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagosEmDia
                    }
                    max={[
                      "pagosEmDia",
                      "pagosAte5Dias",
                      "pagos6a30Dias",
                      "pagosApos31Dias",
                    ]
                      .map((k) =>
                        Number(
                          data?.painelPositivo?.pontualidadePagamento
                            ?.cartaoCredito?.[k] || 0
                        )
                      )
                      .reduce((a, b) => Math.max(a, b), 1)}
                  />
                ) : null}

                <Bar
                  title="Atrasos (até 30 dias)"
                  value={
                    Number(
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagosAte5Dias || 0
                    ) +
                    Number(
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagos6a30Dias || 0
                    )
                  }
                  max={
                    Number(
                      data?.painelPositivo?.pontualidadePagamento?.cartaoCredito
                        ?.pagosEmDia || 0
                    ) || 1
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== PARTICIPAÇÃO SOCIETÁRIA ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <LuBuilding2 className="text-blue-700" />
            <span>Participação em empresas</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.participacaoEmEmpresas) &&
        data?.participacaoEmEmpresas?.length > 0 ? (
          <ParticipacaoEmEmpresas empresas={data?.participacaoEmEmpresas} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== NEGATIVAÇÕES / PROTESTOS ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Negativações Registradas</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.pendenciasFinanceiras) &&
        data?.pendenciasFinanceiras?.length > 0 ? (
          <PendenciasFinanceiras
            quantidadeTotal={resumo.pendenciasFinanceiras.quantidadeTotal}
            valorTotal={resumo.pendenciasFinanceiras.valorTotal}
            pendenciasFinanceiras={data?.pendenciasFinanceiras}
          />
        ) : (
          <NoOcurrence />
        )}

        <div>
          <TitleStripe className="mb-5">
            <div className="flex items-center gap-2">
              <GrDocumentExcel className="text-blue-700" />
              <span>Títulos protestados</span>
            </div>
          </TitleStripe>
          {Array.isArray(data?.protestos) && data?.protestos?.length > 0 ? (
            <Protestos
              quantidadeTotal={resumo.protestos.quantidadeTotal}
              valorTotal={resumo.protestos.valorTotal}
              protestos={data?.protestos}
              temVencimento={false}
            />
          ) : (
            <NoOcurrence />
          )}
        </div>
      </div>

      {/* ===================== CHEQUES ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sustados</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.chequesSustados) &&
        data?.chequesSustados?.length > 0 ? (
          <ChequesSustados chequesSustados={data?.chequesSustados} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.chequesSemFundo) &&
        data?.chequesSemFundo?.length > 0 ? (
          <ChequesSemFundos chequesSemFundos={data?.chequesSemFundo} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== AÇÕES CÍVEIS ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <MdOutlineBalance className="text-blue-700" />
            <span>Ações Cíveis</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.acoesCiveis) && data?.acoesCiveis?.length > 0 ? (
          <AcoesCiveis
            quantidadeTotal={resumo.acoesCiveis.quantidadeTotal}
            acoesCiveis={data?.acoesCiveis}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== AVISOS ===================== */}
      {avisos?.length > 0 && (
        <div>
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <IoMdNotificationsOutline className="text-blue-700" />
              <span>Avisos</span>
            </div>
          </TitleStripe>
          <div className="grid grid-cols-1 gap-2">
            {avisos.map((a, i) => (
              <AvisoCard
                key={i}
                title={a?.titulo || "Aviso"}
                description={a?.mensagem || a}
              />
            ))}
          </div>
        </div>
      )}

      {/* ===================== RESUMO CONSULTAS ANTERIORES ===================== */}
      <div>
        <TitleStripe className="mb-3">
          <div className="flex items-center gap-2">
            <FaRegCalendarAlt className="text-blue-700" />
            <span>Resumo de consultas (últimos meses)</span>
          </div>
        </TitleStripe>
        {data?.resumoConsultasAnteriores ? (
          <TabelaResumoMeses resumo={data.resumoConsultasAnteriores} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== HISTÓRICO DE CONSULTAS (lista) ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.historicoConsultas) &&
        data?.historicoConsultas?.length > 0 ? (
          <div className="flex gap-2 flex-col">
            <div className="flex-1">
              <HistoricoConsultas
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {/* Gráfico opcional (mantido comentado)
            {data?.historicoConsultasResumo?.paresMesAnoHistoricoConsultas?.length > 0 && (
              <ReactApexChart
                className="border border-gray-200 rounded-md w-full mt-3"
                options={...}
                series={[...]}
                type="bar"
                height={175}
              />
            )} */}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              <strong>Endereço:</strong> {fullAddress || "—"}
            </div>
            {fullAddress ? (
              <StreetView address={fullAddress} height={460} />
            ) : (
              <div className="p-3 text-sm text-gray-500">
                Endereço indisponível.
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AcertaCompletoPositivoQuery;
